import React, { useState, useEffect } from 'react';
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Button,
  Stack,
  Paper
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
// Import authenticated API service instead of raw axios
import { default as api } from '../../../services/api';

/**
 * InlineInspectionDetailsForm component
 * This is an inline version of the inspection form that can be embedded within other components
 * 
 * @param {Object} props
 * @param {Object} props.assetDetails - Asset details object 
 * @param {string} props.batchId - Batch ID for linking inspection to batch
 * @param {Function} props.onInspectionSubmit - Callback function called after successful submission
 */
const InlineInspectionDetailsForm = ({ assetDetails, batchId, onInspectionSubmit }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [inspectionTypes, setInspectionTypes] = useState([]);
  const [inspectors, setInspectors] = useState([]);
  const [loadingResources, setLoadingResources] = useState(false);
  const [enhancedAsset, setEnhancedAsset] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    inspection_type_id: '',
    inspector_id: '',
    inspection_date: new Date(),
    notes: '',
    cycle: ''
  });

  // Load inspection types and inspectors when the component mounts
  useEffect(() => {
    if (!assetDetails) return;
    
    // Initialize enhanced asset with the original asset
    setEnhancedAsset(assetDetails);
    
    const fetchResources = async () => {
      setLoadingResources(true);
      try {
        // Check if we have the required IDs
        if (!assetDetails.organization_id) {
          console.warn('Asset is missing organization_id:', assetDetails);
          
          // Try to fetch the full asset details to get the organization_id
          try {
            const assetResponse = await api.get(`/assets/${assetDetails.id}`);
            if (assetResponse.data && assetResponse.data.organization_id) {
              const updatedAsset = {
                ...assetDetails,
                organization_id: assetResponse.data.organization_id,
                organization_name: assetResponse.data.organization_name
              };
              setEnhancedAsset(updatedAsset);
              
              // Continue with the fetching process using the updated asset
              await fetchTypesAndInspectors(updatedAsset);
              setLoadingResources(false);
              return;
            } else {
              throw new Error("Failed to retrieve organization ID for this asset");
            }
          } catch (assetError) {
            console.error("Error fetching additional asset details:", assetError);
            setError('Asset is missing organization ID data. This information is required to create an inspection.');
            setLoadingResources(false);
            return;
          }
        }
        
        // If we have organization_id, proceed with fetching types and inspectors
        await fetchTypesAndInspectors(assetDetails);
        
      } catch (err) {
        console.error('Error fetching resources:', err);
        let errorMessage = 'Failed to load inspection types or inspectors';
        
        if (err.response) {
          errorMessage += `: ${err.response.status} - ${err.response.data?.error || ''}`;
        }
        
        setError(errorMessage);
      } finally {
        setLoadingResources(false);
      }
    };
    
    const fetchTypesAndInspectors = async (asset) => {
      try {
        // Fetch inspection types filtered by asset's organization_id and asset_type_id
        const typesResponse = await api.get('/inspections/types', {
          params: { 
            organization_id: asset.organization_id,
            asset_type_id: asset.asset_type_id
          }
        });
        
        if (Array.isArray(typesResponse.data)) {
          setInspectionTypes(typesResponse.data);
          
          // If no inspection types found, show a message
          if (typesResponse.data.length === 0) {
            setError('No inspection types available for this asset');
          }
        }
        
        // Fetch inspectors (users)
        const inspectorsResponse = await api.get('/users', {
          params: { 
            role: 'inspector',
            organization_id: asset.organization_id 
          }
        });
        
        if (Array.isArray(inspectorsResponse.data)) {
          setInspectors(inspectorsResponse.data);
          
          // If no inspectors found, show a message
          if (inspectorsResponse.data.length === 0) {
            setError('No inspectors available for this organization');
          }
        }
      } catch (err) {
        throw err; // Re-throw to be caught by the calling function
      }
    };
    
    fetchResources();
  }, [assetDetails]);
  
  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  // Handle date change
  const handleDateChange = (newDate) => {
    setFormData({
      ...formData,
      inspection_date: newDate
    });
  };
  
  // Handle form submission
  const handleSubmit = async () => {
    if (!enhancedAsset) {
      setError('No asset selected');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Create the inspection data
      const inspectionData = {
        ...formData,
        asset_id: enhancedAsset.id,
        status: 'draft',
        batch_id: batchId // Link this inspection to the batch
      };
      
      // Send the request to create a new inspection
      const response = await api.post('/inspections', inspectionData);
      
      // Set success status
      setSuccess(true);
      
      // Call the success callback with the new inspection
      if (onInspectionSubmit) {
        onInspectionSubmit({
          ...response.data,
          assetId: enhancedAsset.id,
          batchId
        });
      }
      
      // Reset form data
      setFormData({
        inspection_type_id: '',
        inspector_id: '',
        inspection_date: new Date(),
        notes: '',
        cycle: ''
      });
      
      // Clear success after a delay
      setTimeout(() => {
        setSuccess(false);
      }, 3000);
      
    } catch (err) {
      let errorMessage = 'Failed to create inspection';
      
      if (err.response) {
        errorMessage = `API Error: ${err.response.status} - ${err.response.data?.error || ''}`;
      } else if (err.request) {
        errorMessage = 'No response received from server';
      } else {
        errorMessage = `Error: ${err.message}`;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };
  
  // Check if form is valid
  const isFormValid = () => {
    return (
      formData.inspection_type_id &&
      formData.inspector_id &&
      formData.inspection_date &&
      formData.cycle
    );
  };

  return (
    <Paper elevation={1} sx={{ p: 2, mt: 1, mb: 2, bgcolor: 'background.paper' }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Inspection created successfully!
        </Alert>
      )}
      
      {loadingResources ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
          <CircularProgress size={30} />
        </Box>
      ) : (
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Inspection Details
          </Typography>
          
          <FormControl fullWidth size="small">
            <InputLabel id="inspection-type-label">Inspection Type</InputLabel>
            <Select
              labelId="inspection-type-label"
              id="inspection_type_id"
              name="inspection_type_id"
              value={formData.inspection_type_id}
              onChange={handleChange}
              label="Inspection Type"
              disabled={loading || success}
            >
              {inspectionTypes.length > 0 ? (
                inspectionTypes.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.name}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled value="">
                  No inspection types available for this asset
                </MenuItem>
              )}
            </Select>
          </FormControl>
          
          <FormControl fullWidth size="small">
            <InputLabel id="inspector-label">Inspector</InputLabel>
            <Select
              labelId="inspector-label"
              id="inspector_id"
              name="inspector_id"
              value={formData.inspector_id}
              onChange={handleChange}
              label="Inspector"
              disabled={loading || success}
            >
              {inspectors.length > 0 ? (
                inspectors.map((inspector) => (
                  <MenuItem key={inspector.id} value={inspector.id}>
                    {inspector.full_name || inspector.username}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled value="">
                  No inspectors available for this organization
                </MenuItem>
              )}
            </Select>
          </FormControl>
          
          <FormControl fullWidth size="small">
            <InputLabel id="cycle-label">Inspection Cycle</InputLabel>
            <Select
              labelId="cycle-label"
              id="cycle"
              name="cycle"
              value={formData.cycle}
              onChange={handleChange}
              label="Inspection Cycle"
              disabled={loading || success}
            >
              <MenuItem value="1">Cycle 1</MenuItem>
              <MenuItem value="2">Cycle 2</MenuItem>
              <MenuItem value="3">Cycle 3</MenuItem>
              <MenuItem value="Others">Others</MenuItem>
            </Select>
          </FormControl>
          
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Inspection Date"
              value={formData.inspection_date}
              onChange={handleDateChange}
              disabled={loading || success}
              slotProps={{ 
                textField: { 
                  fullWidth: true,
                  variant: "outlined",
                  size: "small"
                } 
              }}
            />
          </LocalizationProvider>
          
          <TextField
            id="notes"
            name="notes"
            label="Notes"
            multiline
            rows={2}
            value={formData.notes}
            onChange={handleChange}
            fullWidth
            disabled={loading || success}
            variant="outlined"
            size="small"
          />
          
          <Box display="flex" justifyContent="flex-end" mt={1}>
            <Button 
              onClick={handleSubmit} 
              color="primary"
              variant="contained"
              disabled={!isFormValid() || loading || success}
              sx={{ px: 3 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Create Inspection'}
            </Button>
          </Box>
        </Stack>
      )}
    </Paper>
  );
};

export default InlineInspectionDetailsForm; 